import { QueryKeys } from "@/constants/query-keys";
import { OnboardingOptionPill } from "@/features/onboarding/components/onboarding-option-pill";
import { OnboardingPillButton } from "@/features/onboarding/components/onboarding-pill-button";
import { OnboardingStepShellV5 } from "@/features/onboarding/components/onboarding-step-shell-v5";
import { useGetUser } from "@/hooks/requests/user/use-get-user";
import { useUpdateUser } from "@/hooks/requests/user/use-update-user";
import { useToast } from "@/hooks/ui/use-toast";
import { useStepRouteInfo } from "@/hooks/use-step-route-info";
import { triggerHaptic } from "@/utils/haptics";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

const SEX_OPTIONS = [
  { id: "female", label: "Female" },
  { id: "male", label: "Male" },
];

const SexPage = () => {
  const { navigateToNextStep, preloadNextRoute } = useStepRouteInfo();
  const { user } = useGetUser();
  const queryClient = useQueryClient();
  const { updateUserAsync, isUpdating } = useUpdateUser();
  const { showError } = useToast();
  const personalInfo = user?.personalInfo;
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const selected = selectedId ?? personalInfo?.sex ?? null;

  const handleContinue = async () => {
    if (!selected || isUpdating) return;
    triggerHaptic();
    try {
      await Promise.all([
        preloadNextRoute(),
        updateUserAsync({
          firstName: personalInfo?.firstName ?? "",
          lastName: personalInfo?.lastName ?? "",
          dob: personalInfo?.dob ?? "",
          sex: selected,
        }),
      ]);
      queryClient.setQueryData([QueryKeys.USER], (old: object) => ({
        ...old,
        personalInfo: { ...personalInfo, sex: selected },
      }));
      navigateToNextStep();
    } catch (error) {
      showError(error instanceof Error ? error.message : "An unexpected error occurred");
    }
  };

  return (
    <OnboardingStepShellV5
      title="What’s your sex?"
      subtitle="This helps me calculate your starting nutrition targets"
      footer={
        <OnboardingPillButton
          text="Continue"
          onClick={handleContinue}
          disabled={!selected || isUpdating}
        />
      }
    >
      <div className="flex flex-col gap-4">
        {SEX_OPTIONS.map((option) => (
          <OnboardingOptionPill
            key={option.id}
            label={option.label}
            selected={selected === option.id}
            onClick={() => setSelectedId(option.id)}
          />
        ))}
      </div>
    </OnboardingStepShellV5>
  );
};

export default SexPage;
